import { apiClient } from "./_client";
import type { Highlight } from "react-pdf-highlighter-extended";

/**
 * Position and geometric types for PDF highlights
 * These types match the react-pdf-highlighter-extended library format
 */

export interface HighlightRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
  pageNumber: number;
}

export interface HighlightBoundingRect extends HighlightRect {}

export interface HighlightPositionData {
  pageNumber?: number;
  boundingRect: HighlightBoundingRect;
  rects: HighlightRect[];
  /** If true, coordinates are in PDF space and need conversion via viewport */
  usePdfCoordinates?: boolean;
}

/**
 * User info from backend responses
 */
interface UserInfo {
  id: string;
  name: string;
  email: string;
}

/**
 * Base highlight type from react-pdf-highlighter-extended with backend fields
 */
type BaseExtendedHighlight = Highlight & {
  userInfo?: UserInfo;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Text highlight - content and comment both optional. Content carries the
 * matched PDF text when the highlight was produced by a text-layer search
 * (so downstream code can reason about what was matched without re-deriving
 * it from coordinates); overrides the deprecated base `Highlight.content`.
 */
type TextHighlight = BaseExtendedHighlight & {
  type: "text";
  content?: { text: string };
  comment?: { text: string; emoji: string };
};

/**
 * Area highlight - both content and comment are optional
 */
type AreaHighlight = BaseExtendedHighlight & {
  type: "area";
  content?: { text: string };
  comment?: { text: string; emoji: string };
};

/**
 * Frontend discriminated union type for highlights
 */
export type ExtendedHighlight = TextHighlight | AreaHighlight;

/**
 * Backend write format for creating highlights
 * Based on DocumentHighlightWriteSerializer fields
 */
export interface CreateHighlightRequest {
  document: string;
  position: HighlightPositionData;
  content?: { text: string };
  comment?: { text: string; emoji: string };
  type?: "text" | "area";
}

/**
 * Backend write format for updating highlights
 * Based on DocumentHighlightWriteSerializer fields (all optional for PATCH)
 */
export interface UpdateHighlightRequest {
  position?: HighlightPositionData;
  content?: { text: string };
  comment?: { text: string; emoji: string };
  type?: "text" | "area";
}

const BASE_URL = "/api/documents/highlights";

/**
 * Get all highlights for a specific document (from all users)
 */
export const getDocumentHighlights = async (
  documentId: string,
): Promise<ExtendedHighlight[]> => {
  const response = await apiClient.get(
    `${BASE_URL}/by_document/?document_id=${documentId}`,
  );
  return response.data;
};

/**
 * Create a new highlight
 */
export const createHighlight = async (
  highlightData: CreateHighlightRequest,
): Promise<ExtendedHighlight> => {
  const response = await apiClient.post(`${BASE_URL}/`, highlightData);
  return response.data;
};

/**
 * Update an existing highlight
 */
export const updateHighlight = async (
  highlightId: string,
  highlightData: UpdateHighlightRequest,
): Promise<ExtendedHighlight> => {
  const response = await apiClient.patch(
    `${BASE_URL}/${highlightId}/`,
    highlightData,
  );
  return response.data;
};

/**
 * Delete a highlight
 */
export const deleteHighlight = async (highlightId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/${highlightId}/`);
};

/**
 * Get user's own highlights
 */
export const getUserHighlights = async (): Promise<ExtendedHighlight[]> => {
  const response = await apiClient.get(`${BASE_URL}/`);
  return response.data;
};
